import { addSpacesToTitleCase } from "_shared/_utilities/add-spaces-to-title-case";
import { enumKeys, enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import { getProposalName, isPermissionsRow, isTemplateRow } from "permissions/_views/tables/_utilities/utils";
import Permission from "permissions/permission";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import { AccessLevel } from "permissions/resources/types";
import { WorkAgreementField } from "permissions/resources/work/types";
import { ProposalAccess } from "permissions/resources/work/work-permissions-resources";
import { GroupedCellConfig, CardType, MetaControlType } from "permissions/_views/types/permission-types";
import { JSX } from "react";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { ProposalError } from "work/proposals/_store/proposals-redux-slice";

export default function ProposalGroupCell({
  row,
  loadingProposalIds,
  allProposalErrors,
  barebonesProposals,
  renderGroupedPermission,
}: {
  row: PermissionsByGrantee;
  loadingProposalIds: Guid[] | boolean;
  allProposalErrors: ProposalError[];
  barebonesProposals: BarebonesProposalResponse[];
  renderGroupedPermission: (row: PermissionsByGrantee, config: GroupedCellConfig) => JSX.Element;
}): JSX.Element {
  // Determine dynamic title (single proposal -> show its name)
  const proposalResources = row.recentPermissionsPreview
    .filter((p): p is Permission & { resource: ProposalAccess } => p.resource instanceof ProposalAccess)
    .map((p) => p.resource);

  const uniqueProposalIds = Array.from(
    new Set(proposalResources.map((r) => r.proposalId?.value).filter((v): v is string => !!v))
  );
  const title = (() => {
    if (uniqueProposalIds.length !== 1) return undefined;
    const resWithId =
      proposalResources.find((r) => r.proposalId?.value === uniqueProposalIds[0]) ??
      proposalResources.find((r) => !!r.proposalId);
    return resWithId
      ? getProposalName(resWithId, loadingProposalIds, allProposalErrors, barebonesProposals)
      : undefined;
  })();

  return renderGroupedPermission(row, {
    cardType: CardType.Grouped,
    keys: enumKeys(WorkAgreementField).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    candidatesForKey: (row, waKey) => {
      if (isPermissionsRow(row)) {
        return row.recentPermissionsPreview.filter((permission) => {
          if (!(permission.resource instanceof ProposalAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = permission.resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      if (isTemplateRow(row)) {
        return row.resources.filter((resource) => {
          if (!(resource instanceof ProposalAccess)) return false;
          const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
          const fields = resource.fields ?? {};
          return Object.hasOwn(fields, fieldKey);
        });
      }
      return [];
    },
    labelForKey: (waKey) => addSpacesToTitleCase(waKey),
    controlMetaForResource: (resource, waKey) => {
      const fieldKey = WorkAgreementField[waKey as keyof typeof WorkAgreementField];
      const fields = ((resource as ProposalAccess).fields as Partial<Record<WorkAgreementField, AccessLevel>>) ?? {};
      const current = fields[fieldKey];
      const opts = enumKeys(AccessLevel);
      const value = current ? enumKeyOf(AccessLevel, current) : opts[0];
      return { type: MetaControlType.AccessLevel, label: "Access Level", options: opts, value };
    },
    title,
    colSpan: 2,
    rowSpan: 2,
    canEdit: true,
  });
}
